/**
 * MasterDataStore Service - Zustand Store
 * Single Responsibility: Centralized master data storage with snapshot for rollback
 */

import { create } from 'zustand';
import { usePathNavigatorStore } from './PathNavigatorStore';

export interface MasterDataStoreState {
  data: Record<string, any>;
  snapshot: Record<string, any>;
}

export interface MasterDataStoreActions {
  /**
   * Set new master data and create snapshot
   */
  setData: (newData: Record<string, any>) => void;

  /**
   * Get current master data
   */
  getData: () => Record<string, any>;

  /**
   * Get snapshot for rollback
   */
  getSnapshot: () => Record<string, any>;

  /**
   * Update a specific field using path
   */
  updateField: (path: string, value: any) => void;

  /**
   * Reset to snapshot
   */
  reset: () => void;

  /**
   * Clear all data
   */
  clear: () => void;
}

export type MasterDataStore = MasterDataStoreState & MasterDataStoreActions;

export const useMasterDataStore = create<MasterDataStore>((set, get) => ({
  data: {},
  snapshot: {},

  setData: (newData: Record<string, any>): void => {
    set((state) => ({
      snapshot: typeof structuredClone === 'function'
        ? structuredClone(state.data)
        : JSON.parse(JSON.stringify(state.data)),
      data: newData,
    }));
  },

  getData: (): Record<string, any> => {
    return get().data;
  },

  getSnapshot: (): Record<string, any> => {
    return get().snapshot;
  },

  updateField: (path: string, value: any): void => {
    const pathNavigator = usePathNavigatorStore.getState();
    const current = get().data;
    pathNavigator.setValue(current, path, value);
    set({ data: { ...current } });
  },

  reset: (): void => {
    set((state) => ({
      data: typeof structuredClone === 'function'
        ? structuredClone(state.snapshot)
        : JSON.parse(JSON.stringify(state.snapshot)),
    }));
  },

  clear: (): void => {
    set({ data: {}, snapshot: {} });
  },
}));
